import React, { useEffect, useState, forwardRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  styled,
  Fade,
  Zoom,
  useTheme,
} from "@mui/material";
import { getCandidateApplications } from "../../services/applicationService";

// Fade transition for dialog
const Transition = forwardRef(function Transition(props, ref) {
  return <Fade ref={ref} {...props} timeout={300} />;
});

// Styled dialog with frosted-glass effect
const BlurredDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
    border: `2px solid ${theme.palette.primary.main}`,
    background: (theme.vars || theme).palette.background.paper,
    backdropFilter: "blur(8px)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    padding: theme.spacing(5),
  },
}));

export default function MyApplications() {
  const theme = useTheme();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const response = await getCandidateApplications();
        if (response.success) {
          setApplications(response.data);
        }
      } catch (err) {
        console.error("Error fetching applications:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (applications.length === 0) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="text.secondary">
          You haven’t applied to any jobs yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        My Applications
      </Typography>

      <Stack spacing={2}>
        {applications.map((app) => {
          const isSelected = selectedApp?.id === app.id;
          return (
            <Card
              key={app.id}
              sx={{
                borderRadius: 3,
                boxShadow: 2,
                cursor: "pointer",
                transition: "0.2s",
                border: isSelected ? `2px solid ${theme.palette.primary.main}` : "1px solid transparent",
                backgroundColor: isSelected ? theme.palette.action.selected : "inherit",
                "&:hover": {
                  boxShadow: 4,
                  transform: "scale(1.01)",
                },
              }}
              onClick={() => setSelectedApp(app)}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight="bold">
                    {app.job.jobTitle}
                  </Typography>
                  <Chip
                    label={app.status}
                    color={
                      app.status === "SELECTED"
                        ? "success"
                        : app.status === "APPLIED"
                        ? "info"
                        : "default"
                    }
                    variant="outlined"
                  />
                </Box>

                <Typography variant="subtitle2" color="text.secondary">
                  {app.job.department} • {app.job.location}
                </Typography>
                <Typography variant="body2" mt={1}>
                  <b>Type:</b> {app.job.employmentType} | <b>Experience:</b> {app.job.experienceLevel}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="body2" color="text.secondary" noWrap>
                  {app.job.jobSummary}
                </Typography>

                <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                  Applied on: {new Date(app.appliedAt).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* Job Details Dialog */}
      <BlurredDialog
        open={!!selectedApp}
        onClose={() => setSelectedApp(null)}
        maxWidth="md"
        fullWidth
        TransitionComponent={Transition}
      >
        {selectedApp && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: theme.palette.primary.main, textTransform: "uppercase" }}
                >
                  {selectedApp.job.jobTitle}
                </Typography>
                <Chip
                  label={selectedApp.status}
                  color={
                    selectedApp.status === "SELECTED"
                      ? "success"
                      : selectedApp.status === "APPLIED"
                      ? "info"
                      : "default"
                  }
                  variant="filled"
                />
              </Box>
            </DialogTitle>

            <DialogContent dividers>
              <Typography
                variant="subtitle1"
                gutterBottom
                color="text.secondary"
                sx={{ fontWeight: "600", mb: 1 }}
              >
                {selectedApp.job.department} • {selectedApp.job.location}
              </Typography>

              <Stack spacing={0.5} mb={2}>
                <Typography variant="body2">
                  <b>Employment Type:</b> {selectedApp.job.employmentType}
                </Typography>
                <Typography variant="body2">
                  <b>Experience:</b> {selectedApp.job.experienceLevel}
                </Typography>
                <Typography variant="body2">
                  <b>Education:</b> {selectedApp.job.educationRequirements}
                </Typography>
                <Typography variant="body2">
                  <b>Salary:</b> {selectedApp.job.minSalary} – {selectedApp.job.maxSalary} {selectedApp.job.currency}
                </Typography>
                <Typography variant="body2">
                  <b>Applied on:</b> {new Date(selectedApp.appliedAt).toLocaleString()}
                </Typography>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography
                variant="h6"
                sx={{ mt: 2, mb: 1, borderBottom: `2px solid ${theme.palette.primary.main}`, display: "inline-block" }}
              >
                Job Summary
              </Typography>
              <Typography variant="body2" gutterBottom>
                {selectedApp.job.jobSummary}
              </Typography>

              <Typography
                variant="h6"
                sx={{ mt: 2, mb: 1, borderBottom: `2px solid ${theme.palette.primary.main}`, display: "inline-block" }}
              >
                Responsibilities
              </Typography>
              <List dense>
                {selectedApp.job.responsibilities.map((item, i) => (
                  <ListItem key={i} sx={{ pl: 0 }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>

              <Typography
                variant="h6"
                sx={{ mt: 2, mb: 1, borderBottom: `2px solid ${theme.palette.primary.main}`, display: "inline-block" }}
              >
                Required Skills
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                {selectedApp.job.requiredSkills.map((skill, i) => (
                  <Zoom in={!!selectedApp} style={{ transitionDelay: `${i * 50}ms` }} key={i}>
                    <Chip label={skill} variant="outlined" size="small" sx={{ borderRadius: "6px" }} />
                  </Zoom>
                ))}
              </Stack>

              <Typography
                variant="h6"
                sx={{ mt: 2, mb: 1, borderBottom: `2px solid ${theme.palette.primary.main}`, display: "inline-block" }}
              >
                Preferred Skills
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {selectedApp.job.preferredSkills.map((skill, i) => (
                  <Zoom in={!!selectedApp} style={{ transitionDelay: `${i * 50}ms` }} key={i}>
                    <Chip label={skill} color="secondary" variant="outlined" size="small" sx={{ borderRadius: "6px" }} />
                  </Zoom>
                ))}
              </Stack>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setSelectedApp(null)} variant="outlined">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </BlurredDialog>
    </Box>
  );
}
